import { T, useI18n } from '../lib/i18n'
import { Check, KeyRound, ShieldCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Logo } from '../components/Logo'
import { LoadingScreen } from '../components/UI'
import { useAuth } from '../context/AuthContext'
import {
  clearPendingTeamInvitation,
  formatTeamInvitationCode,
  isValidTeamInvitationCode,
  savePendingTeamInvitation,
} from '../lib/invitations'
import { supabase } from '../lib/supabase'

export function TeamJoinPage() {
  const { code: rawCode = '' } = useParams()
  const { session, profile, loading, refreshProfile } = useAuth()
  const navigate = useNavigate()
  const { t } = useI18n()
  const [connecting, setConnecting] = useState(false)
  const code = useMemo(() => formatTeamInvitationCode(rawCode), [rawCode])
  const valid = isValidTeamInvitationCode(code)

  useEffect(() => {
    if (valid) savePendingTeamInvitation(code)
  }, [code, valid])

  if (!valid) return <TeamInviteMessage title="This team invitation is incomplete" message="Ask the company owner or manager to create a new private employee invitation." />
  if (loading) return <LoadingScreen />
  if (!session) return <Navigate to={`/signin?teamInvite=${encodeURIComponent(code)}`} replace />
  if (!profile?.onboarding_completed_at || !profile.default_account_kind) return <Navigate to="/onboarding" replace />
  if (profile.default_account_kind !== 'business') {
    return <TeamInviteMessage title="Use a separate work sign-in" message="A client account cannot also become an employee account. Sign out and use the exact work email named in the invitation." />
  }

  async function connect() {
    if (connecting) return
    setConnecting(true)
    const { error } = await supabase.rpc('redeem_employee_invitation', { invitation_code: code })
    if (error) {
      toast.error(t(error.message))
      setConnecting(false)
      return
    }
    clearPendingTeamInvitation()
    await refreshProfile()
    toast.success(t('Your company team account is connected.'))
    navigate('/', { replace: true })
  }

  return (
    <main className="invite-page">
      <section className="invite-connect-card">
        <Logo />
        <span className="invite-connect-card__icon"><KeyRound size={27} /></span>
        <span className="eyebrow"><T>{"Private employee invitation"}</T></span>
        <h1><T>{"Join your cleaning team"}</T></h1>
        <p><T>{"This code is restricted to the exact verified work email and role chosen by the company."}</T></p>
        <code>{code}</code>
        <button className="button button--primary" onClick={connect} disabled={connecting}>
          {connecting ? t('Joining…') : t('Join company team')}<Check size={17} />
        </button>
        <div className="invite-connect-card__security"><ShieldCheck size={18} /><span><T>{"Single-use, time-limited, and protected by your secure sign-in."}</T></span></div>
      </section>
    </main>
  )
}

function TeamInviteMessage({ title, message }: { title: string; message: string }) {
  const { t } = useI18n()
  return (
    <main className="invite-page">
      <section className="invite-connect-card">
        <Logo />
        <span className="invite-connect-card__icon"><KeyRound size={27} /></span>
        <h1>{t(title)}</h1>
        <p>{t(message)}</p>
        <a className="button button--primary" href="/signin"><T>{"Go to sign in"}</T></a>
      </section>
    </main>
  )
}
